import { useState } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import { Button, Input } from '../../components/ui';

export function ProfileEditModal({ onClose }) {
  const { user, patient, aclCare, refreshUser } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || patient?.phone || '',
    injury_side: aclCare?.injury_side || '',
    injury_date: aclCare?.injury_date || '',
    surgery_date: aclCare?.surgery_date || '',
    surgeon_name: aclCare?.surgeon_name || '',
  });

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Update user profile
      await api.updateUser(user.id, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        acl_care: {
          ...aclCare,
          injury_side: formData.injury_side,
          injury_date: formData.injury_date,
          surgery_date: formData.surgery_date,
          surgeon_name: formData.surgeon_name,
          updated_at: new Date().toISOString()
        }
      });

      await refreshUser();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-end justify-center p-4 sm:items-center sm:p-0">
        <div className="relative bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-lg transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Edit Profile</h2>
            <button
              onClick={onClose}
              className="p-2 -mr-2 rounded-full hover:bg-gray-100"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="First Name"
                value={formData.first_name}
                onChange={(e) => updateFormData('first_name', e.target.value)}
                required
              />
              <Input
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => updateFormData('last_name', e.target.value)}
                required
              />
            </div>

            <Input
              label="Phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => updateFormData('phone', e.target.value)}
              placeholder="(555) 555-5555"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Injury Side
              </label>
              <div className="grid grid-cols-2 gap-3">
                {['left', 'right'].map((side) => (
                  <button
                    key={side}
                    type="button"
                    onClick={() => updateFormData('injury_side', side)}
                    className={`
                      py-2 px-4 rounded-lg border-2 text-center font-medium transition-colors
                      ${formData.injury_side === side
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                      }
                    `}
                  >
                    {side.charAt(0).toUpperCase() + side.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <Input
              label="Injury Date"
              type="date"
              value={formData.injury_date}
              onChange={(e) => updateFormData('injury_date', e.target.value)}
            />

            <Input
              label="Surgery Date"
              type="date"
              value={formData.surgery_date}
              onChange={(e) => updateFormData('surgery_date', e.target.value)}
            />

            <Input
              label="Surgeon's Name"
              value={formData.surgeon_name}
              onChange={(e) => updateFormData('surgeon_name', e.target.value)}
              placeholder="Dr. Smith"
            />

            {error && (
              <p className="text-sm text-red-600">{error}</p>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
                fullWidth
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={loading}
                fullWidth
              >
                Save Changes
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}